import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import Portal from '../components/Portal';
import './AdminPage.css';
import '../components/Modal.css';

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewUser, setShowNewUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [grupoInfo, setGrupoInfo] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    const { data: myProfile } = await supabase.from('profiles').select('grupo_id').eq('id', user.id).single();
    const grupoId = myProfile?.grupo_id;
    const { data } = await supabase.from('profiles').select('*').eq('grupo_id', grupoId).order('name');
    if (grupoId) {
      const { data: grupo } = await supabase.from('grupos').select('max_usuarios, plano, nome').eq('id', grupoId).single();
      setGrupoInfo(grupo);
    }
    setUsers(data || []);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  function flash(type, text) {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 4000);
  }

  async function callEdgeFunction(body) {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
    const res = await fetch(`${supabaseUrl}/functions/v1/create-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'apikey': anonKey,
      },
      body: JSON.stringify(body),
    });
    return res;
  }

  async function handleCreateUser(form) {
    // Verificar limite do plano
    if (grupoInfo && users.length >= grupoInfo.max_usuarios) {
      flash('error', 'Você atingiu o limite. Crie uma nova conta para adicionar mais usuários.');
      return;
    }
    try {
      const { data: myProf } = await supabase.from('profiles').select('grupo_id').eq('id', user.id).single();
      const res = await callEdgeFunction({
        action: 'create',
        username: form.username,
        password: form.password,
        name: form.name,
        role: form.role,
        grupo_id: myProf?.grupo_id,
      });
      const result = await res.json();
      if (!res.ok) { flash('error', result.error || 'Erro ao criar usuário.'); return false; }
      flash('success', `Usuário ${form.name} criado com sucesso!`);
      await loadUsers();
      return true;
    } catch (err) {
      flash('error', 'Erro de conexão: ' + err.message);
      return false;
    }
  }

  async function handleEditUser(form) {
    try {
      const res = await callEdgeFunction({
        action: 'update',
        userId: editUser.id,
        name: form.name,
        role: form.role,
        password: form.password || null,
      });
      const result = await res.json();
      if (!res.ok) { flash('error', result.error || 'Erro ao atualizar usuário.'); return false; }
      flash('success', `Usuário ${form.name} atualizado!`);
      await loadUsers();
      return true;
    } catch (err) {
      flash('error', 'Erro de conexão: ' + err.message);
      return false;
    }
  }

  async function handleDeleteUser(u) {
    if (!window.confirm(`Excluir o usuário "${u.name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      const res = await callEdgeFunction({ action: 'delete', userId: u.id });
      const result = await res.json();
      if (!res.ok) { flash('error', result.error || 'Erro ao excluir usuário.'); return; }
      flash('success', `Usuário ${u.name} excluído.`);
      await loadUsers();
    } catch (err) {
      flash('error', 'Erro de conexão: ' + err.message);
    }
  }

  async function handleUpdateSharing(targetUserId, sharedWithId) {
    await supabase.from('profiles').update({ shared_with: sharedWithId || null }).eq('id', targetUserId);
    flash('success', 'Compartilhamento atualizado!');
    await loadUsers();
  }

  async function handleResetPassword(u) {
    if (!window.confirm(`Redefinir a senha de "${u.name}"? Uma nova senha temporária será gerada.`)) return;
    const novaSenha = Math.random().toString(36).slice(-8);
    try {
      const res = await callEdgeFunction({ action: 'update', userId: u.id, password: novaSenha });
      const result = await res.json();
      if (!res.ok) { flash('error', result.error || 'Erro ao redefinir senha.'); return; }
      flash('success', `Nova senha de ${u.name}: ${novaSenha} — Anote antes de fechar!`);
    } catch (err) {
      flash('error', 'Erro de conexão: ' + err.message);
    }
  }

  async function handleToggleRole(u) {
    const newRole = u.role === 'admin' ? 'user' : 'admin';
    if (u.id === user.id) { flash('error', 'Você não pode alterar seu próprio cargo.'); return; }
    await supabase.from('profiles').update({ role: newRole }).eq('id', u.id);
    flash('success', `${u.name} agora é ${newRole === 'admin' ? 'Administrador' : 'Usuário'}.`);
    await loadUsers();
  }

  const limiteAtingido = grupoInfo && users.length >= grupoInfo.max_usuarios;

  return (
    <div className="admin-page fade-in">
      <div className="admin-header">
        <div>
          <h2 className="admin-title">Administração</h2>
          <p className="admin-sub text-muted text-sm">
            Gerencie usuários e compartilhamento de contas
            {grupoInfo && ` · Grupo: ${grupoInfo.nome}`}
          </p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
          {grupoInfo && (
            <span className="text-xs text-muted">
              👥 {users.length} / {grupoInfo.max_usuarios === 999 ? '∞' : grupoInfo.max_usuarios} usuários
            </span>
          )}
          <button
            className="btn btn-primary"
            onClick={() => {
              if (limiteAtingido) {
                flash('error', 'Você atingiu o limite. Crie uma nova conta para adicionar mais usuários.');
              } else {
                setShowNewUser(true);
              }
            }}
          >
            + Novo Usuário
          </button>
        </div>
      </div>

      {limiteAtingido && (
        <div className="alert alert-error">
          ⚠️ Você atingiu o limite. Crie uma nova conta para adicionar mais usuários.
        </div>
      )}

      {msg.text && (
        <div className={`alert alert-${msg.type === 'error' ? 'error' : 'success'}`}>
          {msg.text}
        </div>
      )}

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'60px 0', gap:12, color:'var(--text2)' }}>
          <div className="spinner" style={{ width:28, height:28 }} />
          <span>Carregando usuários...</span>
        </div>
      ) : (
        <div className="users-grid">
          {users.map(u => (
            <UserCard
              key={u.id}
              u={u}
              currentUserId={user.id}
              allUsers={users}
              onUpdateSharing={handleUpdateSharing}
              onToggleRole={handleToggleRole}
              onEdit={() => setEditUser(u)}
              onDelete={() => handleDeleteUser(u)}
              onResetPassword={() => handleResetPassword(u)}
            />
          ))}
        </div>
      )}

      {showNewUser && (
        <UserModal title="Novo Usuário" onSave={handleCreateUser} onClose={() => setShowNewUser(false)} />
      )}
      {editUser && (
        <UserModal title="Editar Usuário" user={editUser} onSave={handleEditUser} onClose={() => setEditUser(null)} />
      )}
    </div>
  );
}

function UserCard({ u, currentUserId, allUsers, onUpdateSharing, onToggleRole, onEdit, onDelete, onResetPassword }) {
  const [shareOn, setShareOn] = useState(!!u.shared_with);
  const [sharedWith, setSharedWith] = useState(u.shared_with || '');
  const [saving, setSaving] = useState(false);
  const isSelf = u.id === currentUserId;
  const sharesWithMe = allUsers.filter(x => x.shared_with === u.id && x.id !== u.id);
  const options = allUsers.filter(x => x.id !== u.id);

  async function saveSharing() {
    setSaving(true);
    await onUpdateSharing(u.id, shareOn ? sharedWith : null);
    setSaving(false);
  }

  return (
    <div className={`user-card ${isSelf ? 'self' : ''}`}>
      <div className="user-card-top">
        <div className="uc-avatar">{u.name?.[0]?.toUpperCase()}</div>
        <div className="uc-info">
          <div className="uc-name">{u.name} {isSelf && <span className="you-badge">Você</span>}</div>
          <div className="uc-email text-muted text-xs">{u.email?.replace('@fincasa.app', '')}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span className={`role-badge ${u.role}`}>{u.role === 'admin' ? 'Admin' : 'Usuário'}</span>
          <button className="acao-btn edit" onClick={onEdit} title="Editar">✎</button>
          {!isSelf && <button className="acao-btn del" onClick={onDelete} title="Excluir">✕</button>}
        </div>
      </div>

      <div className="share-section">
        <label className="share-toggle-row">
          <input type="checkbox" checked={shareOn} onChange={e => setShareOn(e.target.checked)} className="share-checkbox" />
          <span className="share-label">Compartilhar conta com outro usuário</span>
        </label>
        {shareOn && (
          <select value={sharedWith} onChange={e => setSharedWith(e.target.value)} className="share-select">
            <option value="">— Selecione um usuário —</option>
            {options.map(x => (
              <option key={x.id} value={x.id}>{x.name} ({x.email?.replace('@fincasa.app', '')})</option>
            ))}
          </select>
        )}
        {u.shared_with && !shareOn && (
          <p className="share-info text-xs text-muted">
            Compartilhado com: <strong>{allUsers.find(x => x.id === u.shared_with)?.name || '—'}</strong>
          </p>
        )}
        {sharesWithMe.length > 0 && (
          <p className="share-info text-xs text-muted">
            Recebe acesso de: {sharesWithMe.map(x => x.name).join(', ')}
          </p>
        )}
        <div className="uc-actions">
          <button className="btn btn-primary btn-sm" onClick={saveSharing} disabled={saving || (shareOn && !sharedWith)}>
            {saving ? <><span className="spinner" /> Salvando</> : 'Salvar compartilhamento'}
          </button>
          {!isSelf && (
            <button className="btn btn-ghost btn-sm" onClick={() => onToggleRole(u)}>
              {u.role === 'admin' ? 'Tornar Usuário' : 'Tornar Admin'}
            </button>
          )}
          {!isSelf && (
            <button className="btn btn-ghost btn-sm" onClick={onResetPassword} title="Gerar nova senha temporária">
              🔑 Redefinir Senha
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function UserModal({ title, user: editUser, onSave, onClose }) {
  const [form, setForm] = useState({
    name: editUser?.name || '',
    username: editUser?.email?.replace('@fincasa.app', '') || '',
    password: '',
    role: editUser?.role || 'user',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEdit = !!editUser;

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name) { setError('Informe o nome.'); return; }
    if (!isEdit && !form.username) { setError('Informe o usuário.'); return; }
    if (!isEdit && !form.password) { setError('Informe a senha.'); return; }
    if (form.password && form.password.length < 6) { setError('Senha deve ter pelo menos 6 caracteres.'); return; }
    if (!isEdit && form.username.includes(' ')) { setError('O usuário não pode ter espaços.'); return; }
    setSaving(true);
    const ok = await onSave(form);
    setSaving(false);
    if (ok) onClose();
  }

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-box fade-in" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">{title}</h3>
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label>Nome completo</label>
              <input type="text" placeholder="Ex: Carol Silva" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
            </div>
            {!isEdit && (
              <div className="form-group">
                <label>Usuário</label>
                <input type="text" placeholder="Ex: carol" value={form.username} onChange={e => set('username', e.target.value)} autoCapitalize="none" autoCorrect="off" />
                <span className="field-hint">Login de acesso ao sistema (sem espaços)</span>
              </div>
            )}
            <div className="form-group">
              <label>{isEdit ? 'Nova Senha (deixe em branco para não alterar)' : 'Senha'}</label>
              <input type="password" placeholder={isEdit ? 'Deixe em branco para manter' : 'Mínimo 6 caracteres'} value={form.password} onChange={e => set('password', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Perfil</label>
              <select value={form.role} onChange={e => set('role', e.target.value)}>
                <option value="user">Usuário</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <><span className="spinner" /> Salvando...</> : isEdit ? 'Salvar Alterações' : 'Criar Usuário'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
}
